import type { SignInOutput } from "aws-amplify/auth";
import type { StorageLike } from "./federatedLinkRetry.ts";

/**
 * How someone just signed in, when a passkey would have spared them the step.
 *
 * The sign-in screens record it the moment Cognito signs them in, and
 * PasskeyEnrollmentPrompt takes it once, so the offer to add a passkey comes
 * right after a code or password sign-in. Google and passkey sign-ins record
 * nothing, and neither does a session restored from a refresh token, so those
 * are never asked. Session storage, because the sign-in and the app it opens
 * share the tab.
 */
export type SignInWithoutPasskey = "code" | "password";

const SIGNED_IN_WITH = "spectacle.signedInWithoutPasskey";

/** Record the method, but only once `result` has actually signed someone in. */
export function rememberSignInWithoutPasskey(
    result: SignInOutput,
    method: SignInWithoutPasskey,
    storage: StorageLike | undefined = globalThis.sessionStorage,
): void {
    if (!result.isSignedIn) {
        return;
    }
    try {
        storage?.setItem(SIGNED_IN_WITH, method);
    } catch {
        // Storage unavailable: no nudge this time.
    }
}

/** The method of the sign-in that opened this session, handed out once. */
export function takeSignInWithoutPasskey(
    storage: StorageLike | undefined = globalThis.sessionStorage,
): SignInWithoutPasskey | null {
    try {
        const method = storage?.getItem(SIGNED_IN_WITH) ?? null;
        storage?.removeItem(SIGNED_IN_WITH);
        return method === "code" || method === "password" ? method : null;
    } catch {
        return null;
    }
}

/**
 * Forget an untaken record. CognitoAuthenticator calls this whenever the
 * sign-in screen shows, however the last session ended (sign-out, a refresh
 * token that stopped working), so the next person to sign in on this tab with
 * Google isn't asked on the strength of someone else's code.
 */
export function forgetSignInWithoutPasskey(
    storage: StorageLike | undefined = globalThis.sessionStorage,
): void {
    try {
        storage?.removeItem(SIGNED_IN_WITH);
    } catch {
        // Storage unavailable: nothing to forget.
    }
}
